import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ListChecks, Flag, Target, Hammer } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { statusLabel, statusTone } from "../lib/format";
import { Pager, usePaged } from "../components/Pager";

/**
 * Execution > Operations — portfolio-wide installation view.
 * Lists active projects with task/milestone/goal counts and drill-in links.
 */
const ACTIVE = ["design", "quote_draft", "quote_sent", "approved", "installing"];

export default function Operations() {
  const [projects, setProjects] = useState([]);
  const [allTasks, setAllTasks] = useState({}); // projectId -> tasks[]
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: projs } = await api.get("/projects");
        setProjects(projs || []);
        // Load tasks for every in-flight project, 10 at a time to avoid a burst.
        const subset = (projs || []).filter((p) => ACTIVE.includes(p.status));
        const results = [];
        for (let i = 0; i < subset.length; i += 10) {
          results.push(...await Promise.all(subset.slice(i, i + 10).map((p) =>
            api.get(`/tasks/by-project/${p.id}`).then((r) => [p.id, r.data]).catch(() => [p.id, []]))));
        }
        setAllTasks(Object.fromEntries(results));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const summary = useMemo(() => {
    let tasks = 0, milestones = 0, goals = 0, inProgress = 0, done = 0, blocked = 0;
    for (const arr of Object.values(allTasks)) {
      for (const t of arr) {
        if (t.goal) goals += 1;
        else if (t.milestone) milestones += 1;
        else tasks += 1;
        if (t.status === "in_progress") inProgress += 1;
        else if (t.status === "done") done += 1;
        else if (t.status === "blocked") blocked += 1;
      }
    }
    return { tasks, milestones, goals, inProgress, done, blocked };
  }, [allTasks]);

  const active = useMemo(
    () =>
      projects.filter((p) => ACTIVE.includes(p.status)),
    [projects]
  );
  const { rows: pageRows, pagerProps } = usePaged(active, "eneprox.operations.perPage", active.length);

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="operations-page">
      <div className="mb-8">
        <div className="label flex items-center gap-2">
          <Hammer size={11} /> Execution
        </div>
        <h1 className="font-display text-4xl font-bold mt-1">Operations</h1>
        <p className="muted text-sm mt-2">
          Portfolio-wide view of in-flight projects, tasks, milestones and goals. Click into a project
          to manage its Gantt chart.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8" data-testid="operations-kpis">
        <Kpi label="Active projects" value={active.length} />
        <Kpi label="Tasks" value={summary.tasks} icon={<ListChecks size={12} />} />
        <Kpi label="Milestones" value={summary.milestones} icon={<Flag size={12} />} />
        <Kpi label="Goals" value={summary.goals} icon={<Target size={12} />} />
        <Kpi label="In progress" value={summary.inProgress} tone="signal" />
        <Kpi label="Blocked" value={summary.blocked} tone={summary.blocked ? "err" : "ink"} />
      </div>

      <div className="swiss-card-flat overflow-hidden">
        <div className="overflow-x-auto">
        <table className="data-table" data-testid="operations-table">
          <thead>
            <tr>
              <th>Project</th><th>Customer</th><th>Status</th>
              <th>Tasks</th><th>Milestones</th><th>Goals</th><th>Progress</th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="muted text-center py-10">Loading…</td></tr>
            )}
            {!loading && pageRows.map((p) => {
              const ts = allTasks[p.id] || [];
              const taskCount = ts.filter((t) => !t.milestone && !t.goal).length;
              const msCount = ts.filter((t) => t.milestone && !t.goal).length;
              const goalCount = ts.filter((t) => t.goal).length;
              const taskList = ts.filter((t) => !t.goal);
              const totalProgress =
                taskList.length > 0
                  ? Math.round(taskList.reduce((a, t) => a + (t.progress_pct || 0), 0) / taskList.length)
                  : 0;
              return (
                <tr key={p.id} data-testid={`ops-row-${p.id}`}>
                  <td className="font-medium">{p.name}</td>
                  <td className="muted">{p.customer_name}</td>
                  <td><span className={`tag ${statusTone(p.status)}`}>{statusLabel(p.status)}</span></td>
                  <td className="num font-mono">{taskCount}</td>
                  <td className="num font-mono">{msCount}</td>
                  <td className="num font-mono">{goalCount}</td>
                  <td className="num">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-28 h-1.5 bg-[var(--line-soft)] rounded-full overflow-hidden">
                        <div
                          className="h-full"
                          style={{ width: `${totalProgress}%`, background: "var(--accent)" }}
                        />
                      </div>
                      <span className="font-mono text-xs muted w-9 text-right">{totalProgress}%</span>
                    </div>
                  </td>
                  <td className="num">
                    <Link
                      to={`/projects/${p.id}?tab=tasks`}
                      className="btn-ghost"
                      data-testid={`ops-open-${p.id}`}
                    >
                      Open <ChevronRight size={13} />
                    </Link>
                  </td>
                </tr>
              );
            })}
            {!loading && !active.length && (
              <tr>
                <td colSpan={8} className="muted text-center py-10" data-testid="operations-empty">
                  No in-flight projects yet. Create a project to start tracking installation tasks.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        {!loading && active.length > 0 && <Pager {...pagerProps} testid="operations-pager" />}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, tone }) {
  const bg = tone === "signal" ? "var(--accent-soft)" : tone === "err" ? "#FEE2E2" : "#fff";
  const color = tone === "signal" ? "var(--accent-hover)" : tone === "err" ? "#991B1B" : "var(--ink)";
  return (
    <div className="swiss-card-flat p-4" style={{ background: bg }} data-testid={`ops-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="label flex items-center gap-1">{icon}{label}</div>
      <div className="font-display text-2xl font-semibold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
